// Integrated app switcher + profile cluster — the rightmost element of the
// unified shell's top bar (Z3b), per the appHeaderRebuildProject prototype.
//
// Idle behavior: the cluster rests collapsed — only the active app (icon +
// label) and the avatar. Hovering or keyboard-focusing the cluster expands
// the sibling app icons leftward (pure CSS via `.fk-appstrip--profile`).
// The avatar opens the account menu, which also lists the apps — on ≤640px
// viewports the sibling icons hide and that menu IS the whole switcher.
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useAuth } from '../../auth/AuthProvider';
import { useIsSuperuser } from '../../hooks/useIsSuperuser';
import { useOrgContext } from '../../hooks/useOrgContext';
import { routes } from '../../lib/constants';
import { suiteUrls } from '../../lib/suite-urls';

// App glyphs lifted verbatim from the prototype's symbol sheet.
const IconCoherence = () => (
  <svg
    className="icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2.5 12h4l3-8 6 16 3-8h3" />
  </svg>
);
const IconTeleop = () => (
  <svg
    className="icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 6.5h10a4.8 4.8 0 0 1 4.8 4.8v3.2a3.2 3.2 0 0 1-5.8 1.9L14.6 15H9.4L8 16.4a3.2 3.2 0 0 1-5.8-1.9v-3.2A4.8 4.8 0 0 1 7 6.5Z" />
    <path d="M6.5 11.5h4M8.5 9.5v4M15 12.5h.01M17.5 10.5h.01" />
  </svg>
);
const IconPlatform = () => (
  <svg
    className="icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3.5" y="3.5" width="7" height="7" rx="1" />
    <rect x="13.5" y="3.5" width="7" height="7" rx="1" />
    <rect x="3.5" y="13.5" width="7" height="7" rx="1" />
    <rect x="13.5" y="13.5" width="7" height="7" rx="1" />
  </svg>
);
const IconMetaphysics = () => (
  <svg
    className="icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="7" height="7" rx="1.2" />
    <rect x="14" y="14" width="7" height="7" rx="1.2" />
    <path d="M10 6.5h5.5a2 2 0 0 1 2 2V14" />
  </svg>
);

interface ISuiteApp {
  id: string;
  label: string;
  desc: string;
  icon: ReactNode;
  /** External app URL; null marks this console (Metaphysics) as the active app. */
  url: string | null;
}

// Sibling app URLs resolve from the build's namespace — see lib/suite-urls.ts.
const suiteApps: ISuiteApp[] = [
  {
    id: 'coherence',
    label: 'Coherence',
    desc: 'Telemetry & analytics',
    icon: <IconCoherence />,
    url: suiteUrls.coherence,
  },
  {
    id: 'teleop',
    label: 'Teleop',
    desc: 'Remote device control',
    icon: <IconTeleop />,
    url: suiteUrls.teleop,
  },
  {
    id: 'platform',
    label: 'Platform',
    desc: 'Suite home & administration',
    icon: <IconPlatform />,
    url: suiteUrls.platform,
  },
  {
    id: 'metaphysics',
    label: 'Metaphysics',
    desc: 'AI workflows & agents',
    icon: <IconMetaphysics />,
    url: null,
  },
];

function initialsOf(label: string): string {
  const parts = label
    .trim()
    .split(/[\s@._-]+/)
    .filter(Boolean);
  const first = parts[0]?.charAt(0) ?? '';
  const second = parts[1]?.charAt(0) ?? (first ? (parts[0]?.charAt(1) ?? '') : '');
  return `${first}${second}`.toUpperCase() || '··';
}

export function AppSwitcher() {
  const { email, name, signOut } = useAuth();
  const { orgName } = useOrgContext();
  const isSuper = useIsSuperuser();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointer(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <nav className="fk-appstrip fk-appstrip--profile" aria-label="Applications and account">
      {suiteApps.map((app) =>
        app.url === null ? (
          <Link
            key={app.id}
            to={routes.CHAT}
            className="fk-appstrip__app is-active"
            aria-current="page"
            aria-label={app.label}
          >
            {app.icon}
            <span className="app-label">{app.label}</span>
          </Link>
        ) : (
          <a key={app.id} href={app.url} className="fk-appstrip__app" aria-label={app.label}>
            {app.icon}
            <span className="app-label">{app.label}</span>
          </a>
        ),
      )}
      <span className="fk-appstrip__sep" />
      <div ref={rootRef} style={{ position: 'relative', display: 'inline-flex' }}>
        <button
          type="button"
          className={open ? 'fk-appstrip__profile is-open' : 'fk-appstrip__profile'}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Account and app switcher"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="avatar avatar--xs avatar--sage">
            {initialsOf(name ?? email ?? '··')}
          </span>
        </button>
        {open ? (
          <div
            role="menu"
            className="popover-mock"
            style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              marginTop: 8,
              minWidth: 240,
              zIndex: 60,
            }}
          >
            <div
              style={{
                padding: '8px 10px 10px',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                textAlign: 'left',
              }}
            >
              <div style={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-med)' }}>
                {name ?? email ?? 'Signed in'}
              </div>
              {email && name ? <div className="t-xs t-muted">{email}</div> : null}
              {orgName ? (
                <div className="t-xs t-muted">
                  Org: <code className="t-mono">{orgName}</code>
                </div>
              ) : null}
              {isSuper ? (
                <div style={{ marginTop: 4 }}>
                  <span className="badge badge--tool">Superuser</span>
                </div>
              ) : null}
            </div>
            <div className="menu-divider" />
            <div className="menu-section-label">Switch app</div>
            {suiteApps.map((app) =>
              app.url === null ? (
                <span key={app.id} className="menu-item is-active" role="menuitem">
                  {app.icon} {app.label}
                </span>
              ) : (
                <a key={app.id} href={app.url} className="menu-item" role="menuitem">
                  {app.icon} {app.label}
                </a>
              ),
            )}
            <div className="menu-divider" />
            <Link
              to={routes.SETTINGS}
              className="menu-item"
              onClick={() => setOpen(false)}
              role="menuitem"
            >
              Settings
            </Link>
            <button
              type="button"
              className="menu-item is-destructive"
              style={{
                width: '100%',
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
              }}
              onClick={async () => {
                setOpen(false);
                try {
                  await signOut();
                } catch (err) {
                  console.error(err);
                }
              }}
              role="menuitem"
            >
              Sign out
            </button>
          </div>
        ) : null}
      </div>
    </nav>
  );
}
